/**
 * useWlTenantMasterCustomer — vínculo IRREVERSÍVEL entre o tenant filho
 * e um cliente (crm_customer) do tenant master.
 *
 * Encapsula 3 RPCs:
 *  - wl_search_master_crm_customers
 *  - wl_set_tenant_master_customer (vínculo permanente)
 *  - wl_get_tenant_master_customer
 *
 * RELEASE: 2026-06-14 v1.0.0
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { createLogger } from "@/lib/logger";

const log = createLogger("useWlTenantMasterCustomer");

// ---------------------------------------------------------------------------
// Tipagens
// ---------------------------------------------------------------------------
export interface WlMasterCustomerSearchResult {
  id: string;
  display_name: string | null;
  doc: string | null;
  customer_type: string | null;
  cidade: string | null;
  estado: string | null;
}

export interface WlTenantMasterCustomer {
  crm_customer_id: string;
  display_name: string | null;
  doc: string | null;
  customer_type: string | null;
  cidade: string | null;
  estado: string | null;
  locked: boolean;
}

// ---------------------------------------------------------------------------
// Chaves de cache
// ---------------------------------------------------------------------------
const masterCustomerKey = (tenantId?: string | null) => [
  "wl-tenant-master-customer",
  tenantId,
];

// ---------------------------------------------------------------------------
// 1) Busca clientes do master disponíveis para vínculo
// ---------------------------------------------------------------------------
export function useWlMasterCustomerSearch(
  tenantId?: string | null,
  search?: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ["wl-master-customer-search", tenantId, search ?? null],
    enabled: !!tenantId && enabled,
    staleTime: 15 * 1000,
    queryFn: async (): Promise<WlMasterCustomerSearchResult[]> => {
      const { data, error } = await supabase.rpc(
        "wl_search_master_crm_customers" as never,
        {
          p_tenant_id: tenantId,
          p_search: search?.trim() || null,
          p_limit: 30,
        } as never,
      );
      if (error) {
        log.error("Erro ao buscar clientes do master", { error });
        throw error;
      }
      return (data || []) as WlMasterCustomerSearchResult[];
    },
  });
}

// ---------------------------------------------------------------------------
// 2) Cliente atualmente vinculado
// ---------------------------------------------------------------------------
export function useWlTenantMasterCustomer(tenantId?: string | null) {
  return useQuery({
    queryKey: masterCustomerKey(tenantId),
    enabled: !!tenantId,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<WlTenantMasterCustomer | null> => {
      const { data, error } = await supabase.rpc(
        "wl_get_tenant_master_customer" as never,
        { p_tenant_id: tenantId } as never,
      );
      if (error) throw error;
      const arr = (data ?? []) as any[];
      return Array.isArray(arr) && arr.length > 0
        ? (arr[0] as WlTenantMasterCustomer)
        : null;
    },
  });
}

// ---------------------------------------------------------------------------
// 3) Mutação: vincular (irreversível)
// ---------------------------------------------------------------------------
export function useWlSetTenantMasterCustomer(tenantId?: string | null) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (crmCustomerId: string) => {
      const { data, error } = await supabase.rpc(
        "wl_set_tenant_master_customer" as never,
        {
          p_tenant_id: tenantId,
          p_crm_customer_id: crmCustomerId,
        } as never,
      );
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      // Invalida cache do cliente vinculado E do plan binding (depende dele)
      qc.invalidateQueries({ queryKey: masterCustomerKey(tenantId) });
      qc.invalidateQueries({ queryKey: ["wl-tenant-plan-binding", tenantId] });
      qc.invalidateQueries({ queryKey: ["wl-primary-eligible-subs", tenantId] });
      qc.invalidateQueries({ queryKey: ["wl-extra-eligible-subs", tenantId] });
    },
  });
}
